import { defineStore } from "pinia";
import { ref, computed, watch, ComputedRef, reactive } from "vue";
import {
	RuleSetRule,
	Rule,
	RuleDimension,
	isRuleSetRule,
} from "@/modules/rule_engine/ruleEngineClasses";
import RuleModelVersionDTO = App.Data.RuleEngine.RuleModelVersionDTO;
import RuleSetDTO = App.Data.RuleEngine.Rules.RuleSetDTO;
import RuleSetRuleDTO = App.Data.RuleEngine.Rules.RuleSetRuleDTO;
import AiSummaryStatus = App.Enums.AiSummaryStatus;
import { useApiClient } from "@/api/client";
import ABTestDTO = App.Data.ABTest.ABTestDTO;
import RuleSetVersionType = App.Enums.RuleSetVersionType;
import BexTodoDO = Modules.BookingExperts.Data.BexTodoDO;
import BexLabelDO = Modules.BookingExperts.Data.BexLabelDO;

/**
 * Kept out of `parsedModelTypes`/`rawRuleModelVersion.rule_sets` on purpose:
 * this is background-job status that changes on its own (pending -> processing
 * -> completed), so folding it into the editable tree would leak into the
 * dirty-check/undo-history diff while a user is actively editing.
 */
export type RuleSetAiSummary = {
	ai_summary: string | null;
	ai_summary_status: AiSummaryStatus | null;
};

type RuleEngineState = {
	rawRuleModelVersion?: RuleModelVersionDTO;
	parsedModelTypes: Partial<Record<RuleSetVersionType, RuleSetRule>>;
	ruleDimensions: RuleDimension[];
	displayedGroupUuid?: string;
	selectedRules: string[];
	isProcessingBEResponse: boolean;
	abTests: ABTestDTO[];
	selectedModelVersionType?: RuleSetVersionType;
	values_for_action_type: {
		add_task_to_bex_reservation: BexTodoDO[];
		add_label_to_bex_reservation: BexLabelDO[];
	};
	ruleSetAiSummaries: Record<string, RuleSetAiSummary>;
};

/**
 * `JSON.stringify` replacer that sorts object keys, so replacing a node with an
 * equal one in a different key order doesn't read as a change.
 */
const sortKeys = (_key: string, value: unknown) => {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return value;
	}

	const source = value as Record<string, unknown>;

	return Object.fromEntries(
		Object.keys(source)
			.sort()
			.map((key) => [key, source[key]])
	);
};

export const useRuleEnginedStore = defineStore("ruleEngineStore", () => {
	function createRuleEngineStore(
		type: RuleSetVersionType = "per_hour"
	): RuleEngineState {
		return {
			rawRuleModelVersion: null,
			parsedModelTypes: {},
			ruleDimensions: [],
			displayedGroupUuid: null,
			selectedRules: [],
			isProcessingBEResponse: true,
			abTests: [],
			selectedModelVersionType: type,
			values_for_action_type: {
				add_task_to_bex_reservation: [],
				add_label_to_bex_reservation: [],
			},
			ruleSetAiSummaries: {},
		};
	}

	const ruleEngineState = ref(createRuleEngineStore());

	/**
	 * Serialises everything the builder can edit: the rule trees, plus the
	 * version fields edited outside them (name, output variables).
	 *
	 * Reads deliberately go through Vue's reactive proxies rather than through
	 * `toRawDeep`. Unwrapping first would read raw objects, registering no
	 * dependencies, so `parsedModelHasChanges` would cache its first result and
	 * never see an edit.
	 */
	const buildModelSnapshot = (): string => {
		const { parsedModelTypes, rawRuleModelVersion } = ruleEngineState.value;

		return JSON.stringify(
			{
				rule_sets: parsedModelTypes ?? {},
				name: rawRuleModelVersion?.name ?? null,
				output_variables: rawRuleModelVersion?.output_variables ?? {},
			},
			sortKeys
		);
	};

	const savedModelSnapshot = ref<string | null>(null);

	/** Marks the current model as matching what's persisted. */
	const markModelAsSaved = () => {
		savedModelSnapshot.value = buildModelSnapshot();
	};

	/* ------------------------------------------------------------- history */

	const currentSnapshot = computed(() => buildModelSnapshot());

	const history = ref<string[]>([]);
	const historyIndex = ref<number>(-1);

	const canUndo = computed(() => historyIndex.value > 0);
	const canRedo = computed(() => historyIndex.value < history.value.length - 1);

	/**
	 * Rapid edits (typing a name) coalesce into one history entry rather than one
	 * per keystroke, so undo steps through actions instead of characters.
	 */
	const HISTORY_COALESCE_MS = 400;
	let coalesceTimer: ReturnType<typeof setTimeout> | null = null;

	const pushHistoryEntry = (snapshot: string) => {
		// Also swallows the echo from restoring: after undo/redo the current
		// snapshot already equals the entry we moved to.
		if (snapshot === history.value[historyIndex.value]) return;

		history.value = [
			...history.value.slice(0, historyIndex.value + 1),
			snapshot,
		];
		historyIndex.value = history.value.length - 1;
	};

	watch(currentSnapshot, (snapshot) => {
		if (coalesceTimer) clearTimeout(coalesceTimer);

		coalesceTimer = setTimeout(
			() => pushHistoryEntry(snapshot),
			HISTORY_COALESCE_MS
		);
	});

	/** Seeds history with the loaded model as the state undo bottoms out at. */
	const beginHistory = () => {
		if (coalesceTimer) clearTimeout(coalesceTimer);
		history.value = [buildModelSnapshot()];
		historyIndex.value = 0;
	};

	const applySnapshot = (snapshot: string) => {
		const parsed = JSON.parse(snapshot);

		// Both sides are serialised the same way, so this compares structure
		// rather than key order.
		const restoredTypes = parsed.rule_sets as Partial<
			Record<RuleSetVersionType, unknown>
		>;

		const changedType = (
			Object.keys(restoredTypes) as RuleSetVersionType[]
		).find(
			(versionType) =>
				JSON.stringify(restoredTypes[versionType], sortKeys) !==
				JSON.stringify(
					ruleEngineState.value.parsedModelTypes[versionType],
					sortKeys
				)
		);

		// Plain data throughout, so restoring is a straight assignment — no
		// re-parsing of the rule tree is required.
		ruleEngineState.value.parsedModelTypes = parsed.rule_sets;

		// Restoring a version type the user isn't looking at would read as
		// nothing happening, so follow the change onto its tab.
		if (changedType) {
			ruleEngineState.value.selectedModelVersionType = changedType;
		}

		if (ruleEngineState.value.rawRuleModelVersion) {
			ruleEngineState.value.rawRuleModelVersion.name = parsed.name;
			ruleEngineState.value.rawRuleModelVersion.output_variables =
				parsed.output_variables;
		}

		// The group being viewed may not exist in the restored tree.
		const displayedUuid = ruleEngineState.value.displayedGroupUuid;
		if (
			displayedUuid &&
			displayedUuid !== parsedModelType.value?.uuid &&
			!findRuleByUuid(
				parsedModelType.value?.rule_set?.rules ?? [],
				displayedUuid
			)
		) {
			ruleEngineState.value.displayedGroupUuid =
				parsedModelType.value?.uuid ?? null;
		}

		ruleEngineState.value.selectedRules = [];
	};

	const undo = () => {
		if (coalesceTimer) clearTimeout(coalesceTimer);
		if (!canUndo.value) return;

		historyIndex.value -= 1;
		applySnapshot(history.value[historyIndex.value]);
	};

	const redo = () => {
		if (coalesceTimer) clearTimeout(coalesceTimer);
		if (!canRedo.value) return;

		historyIndex.value += 1;
		applySnapshot(history.value[historyIndex.value]);
	};

	/**
	 * True when the model differs from the last saved snapshot.
	 *
	 * This replaces a deep watcher that only ever set a flag to `true`: it could
	 * not un-dirty itself, fired on no-op edits, and never saw changes outside
	 * `parsedModelTypes` — so renaming a version or editing output variables left
	 * the save button disabled unless a component set the flag by hand.
	 */
	const parsedModelHasChanges = computed(
		() =>
			savedModelSnapshot.value !== null &&
			currentSnapshot.value !== savedModelSnapshot.value
	);

	const modelVersionTypes = computed(
		(): RuleSetVersionType[] =>
			ruleEngineState.value?.rawRuleModelVersion?.rule_sets?.map(
				(versionRuleSet) => versionRuleSet.version_type
			) ?? []
	);

	/**
	 * Falls back to the model's first version type when the current selection
	 * isn't one the loaded model actually has. Call after loading a version.
	 *
	 * This used to be an assignment inside the `modelVersionTypes` computed,
	 * which reset the selection on every re-evaluation — clobbering a selection
	 * the caller had deliberately set — and threw when no version was loaded yet.
	 */
	const ensureSelectedModelVersionTypeIsValid = () => {
		const availableTypes = modelVersionTypes.value;

		if (availableTypes.length === 0) return;
		if (availableTypes.includes(ruleEngineState.value.selectedModelVersionType))
			return;

		ruleEngineState.value.selectedModelVersionType = availableTypes[0];
	};

	const findRuleDimensionById = (uuid: string): RuleDimension => {
		const { ruleDimensions } = ruleEngineState.value;
		return ruleDimensions.find((rule) => rule.uuid === uuid);
	};

	const findRuleByUuid = (rules: Rule[], targetUuid: string) => {
		for (const rule of rules) {
			if (rule.uuid === targetUuid) {
				return rule;
			}

			// If not a group, no need to try and go deeper
			if (!isRuleSetRule(rule)) continue;

			const nestedRules: Rule[] = rule.rule_set?.rules ?? [];
			const foundGroupRule = findRuleByUuid(nestedRules, targetUuid);
			if (foundGroupRule) return foundGroupRule;
		}

		return null;
	};

	const availableActionVariables = computed(() => {
		return (
			activeRuleModelVersion.value?.output_variables[
				ruleEngineState.value.selectedModelVersionType
			] || []
		);
	});

	const parsedModelType = computed(() => {
		const { selectedModelVersionType, parsedModelTypes } =
			ruleEngineState.value;

		return parsedModelTypes[selectedModelVersionType];
	});

	const displayedRuleSetRule = computed((): RuleSetRule => {
		const { displayedGroupUuid } = ruleEngineState.value;

		if (displayedGroupUuid) {
			const foundGroupRule = findRuleByUuid(
				parsedModelType.value.rule_set.rules,
				displayedGroupUuid
			);
			if (foundGroupRule) return foundGroupRule;
		}

		return parsedModelType.value;
	});

	const findParentPath = (
		group: RuleSetRule,
		targetUuid: string,
		path: RuleSetRule[] = []
	): RuleSetRule[] | null => {
		const newPath = [...path, group];

		if (group.uuid === targetUuid) {
			return newPath;
		}

		for (const rule of group.rule_set?.rules ?? []) {
			if (!isRuleSetRule(rule)) continue;

			const result = findParentPath(rule, targetUuid, newPath);
			if (result) return result;
		}

		return null;
	};

	const parentRuleSetRules = computed((): RuleSetRule[] => {
		const { displayedGroupUuid } = ruleEngineState.value;

		if (!displayedGroupUuid || !parsedModelType.value) return [];

		const result = findParentPath(parsedModelType.value, displayedGroupUuid);
		return result ? result.slice(0, -1) : [];
	});

	const allVisibleRuleSetRules = computed((): RuleSetRule[] => {
		const { selectedRules } = ruleEngineState.value;

		const collected: RuleSetRule[] = [];

		function collectRules(rule: RuleSetRule) {
			if (selectedRules.includes(rule.uuid)) {
				return;
			}

			collected.push(rule);

			for (const childRule of rule.rule_set?.rules ?? []) {
				if (isRuleSetRule(childRule)) {
					collectRules(childRule);
				}
			}
		}

		collectRules(parsedModelType.value);

		return collected;
	});

	const activeRuleModelVersion: ComputedRef<RuleModelVersionDTO> = computed(
		() => {
			const { rawRuleModelVersion } = ruleEngineState.value;
			return rawRuleModelVersion;
		}
	);

	const resetState = (type?: RuleSetVersionType) => {
		ruleEngineState.value = createRuleEngineStore(type ?? "per_hour");
		savedModelSnapshot.value = null;
		history.value = [];
		historyIndex.value = -1;
	};

	/**
	 * Walks the raw (unparsed) rule set tree — not `parsedModelTypes` — so this
	 * never touches the editable classes the dirty-check/undo-history diff over.
	 */
	const collectRuleSetAiSummaries = (rawVersion?: RuleModelVersionDTO) => {
		const summaries: Record<string, RuleSetAiSummary> = {};

		const visit = (ruleSet?: RuleSetDTO) => {
			if (!ruleSet) return;

			summaries[ruleSet.uuid] = {
				ai_summary: ruleSet.ai_summary,
				ai_summary_status: ruleSet.ai_summary_status,
			};

			const rules = Array.isArray(ruleSet.rules)
				? ruleSet.rules
				: Object.values(ruleSet.rules ?? {});

			for (const rule of rules) {
				if (rule.type === "rule_set") {
					visit((rule as RuleSetRuleDTO).rule_set);
				}
			}
		};

		(rawVersion?.rule_sets ?? []).forEach(visit);

		ruleEngineState.value.ruleSetAiSummaries = summaries;
	};

	const ruleSetAiSummary = (uuid?: string): RuleSetAiSummary | undefined =>
		uuid ? ruleEngineState.value.ruleSetAiSummaries[uuid] : undefined;

	/** Finds the `RuleSetRule` whose *nested* `rule_set.uuid` matches — the same
	 *  id `ruleSetAiSummary`/`collectRuleSetAiSummaries` key their maps on. */
	const findRuleSetRuleByInnerUuid = (
		root: RuleSetRule | undefined,
		targetUuid: string
	): RuleSetRule | null => {
		if (!root) return null;
		if (root.rule_set?.uuid === targetUuid) return root;

		for (const rule of root.rule_set?.rules ?? []) {
			if (!isRuleSetRule(rule)) continue;

			const found = findRuleSetRuleByInnerUuid(rule, targetUuid);
			if (found) return found;
		}

		return null;
	};

	/**
	 * True when the rule set identified by `uuid` (or any of its descendants)
	 * differs from the last saved snapshot — including a brand new, never-saved
	 * rule set. An existing AI summary describes the last-saved state, so this
	 * drives hiding it rather than showing something potentially stale.
	 *
	 * Deliberately scoped to one rule set rather than reusing the blanket
	 * `parsedModelHasChanges`: a page that keeps multiple rule sets mounted at
	 * once (e.g. Pro Loyalty's per-scenario tabs) would otherwise hide an
	 * unrelated, still-accurate summary just because a sibling changed.
	 */
	const ruleSetHasUnsavedChanges = (uuid?: string): boolean => {
		if (!uuid || savedModelSnapshot.value === null) return false;

		const { selectedModelVersionType, parsedModelTypes } =
			ruleEngineState.value;

		const currentNode = findRuleSetRuleByInnerUuid(
			parsedModelTypes[selectedModelVersionType],
			uuid
		);
		if (!currentNode) return false;

		const savedRuleSets = (JSON.parse(savedModelSnapshot.value).rule_sets ??
			{}) as Partial<Record<RuleSetVersionType, RuleSetRule>>;
		const savedNode = findRuleSetRuleByInnerUuid(
			savedRuleSets[selectedModelVersionType],
			uuid
		);

		if (!savedNode) return true;

		return (
			JSON.stringify(currentNode, sortKeys) !==
			JSON.stringify(savedNode, sortKeys)
		);
	};

	/** Applied when a `RuleEngineAiSummaryGenerated` broadcast event arrives. */
	const applyAiSummaryEvent = (event: {
		target_type: "rule_model_version" | "rule_set";
		target_uuid: string;
		ai_summary: string | null;
		ai_summary_status: AiSummaryStatus;
	}) => {
		if (event.target_type === "rule_model_version") {
			if (ruleEngineState.value.rawRuleModelVersion) {
				ruleEngineState.value.rawRuleModelVersion.ai_summary = event.ai_summary;
				ruleEngineState.value.rawRuleModelVersion.ai_summary_status =
					event.ai_summary_status;
			}
			return;
		}

		ruleEngineState.value.ruleSetAiSummaries[event.target_uuid] = {
			ai_summary: event.ai_summary,
			ai_summary_status: event.ai_summary_status,
		};
	};

	const dimensionValueCache = reactive<
		Record<string, { display_name: string; value: string | null }[]>
	>({});

	const pendingDimensionValueRequests: Record<string, Promise<void>> = {};

	const getDimensionValues = async (
		dimensionUuid: string
	): Promise<{ display_name: string; value: string | null }[]> => {
		if (dimensionValueCache[dimensionUuid] === undefined) {
			pendingDimensionValueRequests[dimensionUuid] ??= useApiClient(
				"ruleEngine"
			)
				.getDimensionValues(dimensionUuid)
				.then((response) => {
					if (response.isOk()) {
						dimensionValueCache[dimensionUuid] = response.value;
					}
				})
				.finally(() => delete pendingDimensionValueRequests[dimensionUuid]);

			await pendingDimensionValueRequests[dimensionUuid];
		}

		return dimensionValueCache[dimensionUuid];
	};

	const hierarchyDefinitionCache: Record<
		string,
		App.Data.Hierarchy.HierarchyDefinitionDO[]
	> = {};

	const getHierarchyDefinitions = async (
		dimensionUuid: string
	): Promise<App.Data.Hierarchy.HierarchyDefinitionDO[]> => {
		if (hierarchyDefinitionCache[dimensionUuid] === undefined) {
			const response = await useApiClient("ruleEngine").getHierarchyDefinitions(
				dimensionUuid
			);

			if (response.isOk()) {
				hierarchyDefinitionCache[dimensionUuid] = response.value;
			}
		}

		return hierarchyDefinitionCache[dimensionUuid] ?? [];
	};

	const hierarchyTreeCache: Record<
		string,
		App.Data.Explorer.Filter.QuickFilterNodeDTO | null
	> = {};

	/**
	 * The root of one tree in the explorer's shape, or null when it could not be loaded. Failures are
	 * not cached, so reopening the condition tries again.
	 */
	const getHierarchyTree = async (
		dimensionUuid: string,
		hierarchyDefinitionId: number
	): Promise<App.Data.Explorer.Filter.QuickFilterNodeDTO | null> => {
		const cacheKey = `${dimensionUuid}.${hierarchyDefinitionId}`;

		if (hierarchyTreeCache[cacheKey] === undefined) {
			const response = await useApiClient("ruleEngine").getHierarchyTree(
				dimensionUuid,
				hierarchyDefinitionId
			);

			if (!response.isOk()) {
				return null;
			}

			hierarchyTreeCache[cacheKey] = response.value[0] ?? null;
		}

		return hierarchyTreeCache[cacheKey];
	};

	return {
		ruleEngineState,
		displayedRuleSetRule,
		parentRuleSetRules,
		allVisibleRuleSetRules,
		activeRuleModelVersion,
		availableActionVariables,
		modelVersionTypes,
		ensureSelectedModelVersionTypeIsValid,
		parsedModelHasChanges,
		markModelAsSaved,
		beginHistory,
		undo,
		redo,
		canUndo,
		canRedo,
		resetState,
		findRuleDimensionByUuid: findRuleDimensionById,
		findRuleByUuid,
		getDimensionValues,
		dimensionValueCache,
		getHierarchyDefinitions,
		getHierarchyTree,
		parsedModelType,
		collectRuleSetAiSummaries,
		ruleSetAiSummary,
		ruleSetHasUnsavedChanges,
		applyAiSummaryEvent,
	};
});
